// Access check for the director dashboard, shared by the page and the entry cards.
// Backend requires: is_staff + feature utils.reports.analytics + role permissions analytics.*.
import { useEffect, useState } from 'react';
import analyticsService, { currentRegion } from './analyticsService';
import { AnalyticsPermissions } from './types/analytics';
import keycloak from '../keycloak';

export type AnalyticsAccessState = 'loading' | 'allowed' | 'denied' | 'error';

export interface AnalyticsAccessResult {
  state: AnalyticsAccessState;
  permissions: AnalyticsPermissions;
  region: string;
}

/** Endpoints the dashboard needs; each maps to permission "analytics.<action>". */
export const DASHBOARD_ACTIONS = ['summary', 'monthly', 'products', 'top_clients', 'receivables', 'staff_credits'];

export const evaluatePermissions = (permissions: AnalyticsPermissions | null | undefined): boolean => {
  if (!permissions || typeof permissions !== 'object') return false;
  // At least the KPI snapshot must be allowed; other sections degrade per-endpoint (403 → region marked).
  return permissions['analytics.summary'] === true;
};

const cached = new Map<string, { promise: Promise<AnalyticsAccessResult>; expires: number }>();

export const checkAnalyticsAccess = (force = false): Promise<AnalyticsAccessResult> => {
  const region = currentRegion();
  const key = `${keycloak.subject || ''}:${region}`;
  const entry = cached.get(key);
  if (entry && entry.expires > Date.now() && !force) return entry.promise;
  const promise = Promise.resolve()
    .then(() => analyticsService.getPermissions(region))
    .then((res) => ({
      state: (evaluatePermissions(res.data) ? 'allowed' : 'denied') as AnalyticsAccessState,
      permissions: res.data || {},
      region,
    }))
    .catch((error: unknown) => {
      const e = error as { response?: { status?: number } };
      const status = e?.response?.status;
      // 403 (no feature / not staff) and 404 (feature not registered for the user) = no access.
      const state: AnalyticsAccessState = status === 403 || status === 404 ? 'denied' : 'error';
      if (state === 'error') cached.delete(key); // retry next time on transient errors
      return { state, permissions: {}, region };
    });
  cached.set(key, { promise, expires: Date.now() + 60000 });
  return promise;
};

/** `enabled = false` skips the request (e.g. appMenu already lacks utils.reports) and reports "denied". */
export const useAnalyticsAccess = (enabled = true): AnalyticsAccessResult => {
  const region = currentRegion();
  const subject = keycloak.subject;
  const [result, setResult] = useState<AnalyticsAccessResult>({
    state: enabled ? 'loading' : 'denied',
    permissions: {},
    region: currentRegion(),
  });
  useEffect(() => {
    if (!enabled) {
      setResult((r) => ({ ...r, state: 'denied' }));
      return;
    }
    let alive = true;
    setResult((r) => ({ ...r, state: 'loading' }));
    checkAnalyticsAccess().then((r) => alive && setResult(r));
    return () => {
      alive = false;
    };
  }, [enabled, region, subject]);
  return result;
};
